import '../LandingPage.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, provider } from '../firebase';
import { signInWithPopup } from 'firebase/auth';
import SiteService from './SiteService';
import FreeConsumptionStepper from './Rating/FreeConsumptionStepper';
import {
  AboutSubMeterSection,
  CommonMistakesSection,
  FaqSection,
  LateReadingExampleSection,
  RecordManagementSection,
  TieredTariffExampleSection,
  TimeOfUseExampleSection,
  UseCasesSection,
  WhySimpleRatioIsNotEnoughSection
} from './LandingContentSections';

const initializedAdElements = new WeakSet();
const AdSenseUnit = () => {
  const insRef = useRef(null);
  useEffect(() => {
    const element = insRef.current;
    if (!element || initializedAdElements.has(element) || element.dataset.adsbygoogleStatus) return;
    initializedAdElements.add(element);
    try { window.adsbygoogle = window.adsbygoogle || []; window.adsbygoogle.push({}); }
    catch (error) { console.debug('AdSense birimi başlatılamadı', error); }
  }, []);
  return (
    <section className="adsense-section" aria-label="Sponsorlu reklam">
      <div className="adsense-label">Sponsorlu</div>
      <ins ref={insRef} className="adsbygoogle adsense-landing" style={{ display: 'block' }}
        data-ad-client="ca-pub-7115067061462299" data-ad-slot="6411716550"
        data-ad-format="auto" data-full-width-responsive="true" />
    </section>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [isCalculationReady, setIsCalculationReady] = useState(false);
  const handleCalculationResultReady = useCallback(() => setIsCalculationReady(true), []);

  const handleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      await SiteService.addUser({ uid: user.uid, email: user.email, displayName: user.displayName, provider: 'google' });
      navigate('/');
    } catch (error) { console.error('Google ile giriş yapılamadı:', error); }
  };

  const scrollToCalculator = () => {
    document.getElementById('calculator-title')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="landing-page">
      <main className="landing-content">

        {/* ── HERO ── */}
        <header className="lp-hero" aria-label="Süzme Sayaç tanıtımı">
          <div className="lp-hero-text">
            <div className="lp-hero-badge">⚡ Elektrik Faturası Paylaşım Aracı</div>
            <h1>
              <span>Süzme Sayaç</span> ile adil
              fatura paylaşımı
            </h1>
            <p>
              Ortak elektrik faturasını tarife ve sayaç tüketimlerini dikkate alarak anlaşılır biçimde paylaştırın.
              Kademeli tarife, çok zamanlı tarife ve öncelik seçimleriyle.
            </p>
            <div className="lp-hero-actions">
              <button className="lp-btn-primary" onClick={scrollToCalculator} id="hero-cta-calculator">
                ⚡ Hızlı Hesapla
              </button>
              <button className="lp-btn-secondary" onClick={handleLogin} id="hero-cta-login">
                Google ile Giriş Yap
              </button>
            </div>
          </div>
          <div className="lp-hero-visual" aria-hidden="true">
            <img
              src="/img-hero.png"
              alt="Süzme sayaç elektrik faturası paylaşım şeması"
              className="lp-hero-img"
            />
          </div>
        </header>

        {/* ── İSTATİSTİKLER ── */}
        <div className="lp-stats" aria-label="Uygulama özellikleri">
          <div className="lp-stat">
            <span className="lp-stat-value">3</span>
            <span className="lp-stat-label">Paylaşım yöntemi</span>
          </div>
          <div className="lp-stat">
            <span className="lp-stat-value">∞</span>
            <span className="lp-stat-label">Süzme sayaç desteği</span>
          </div>
          <div className="lp-stat">
            <span className="lp-stat-value">0 ₺</span>
            <span className="lp-stat-label">Hızlı hesaplama için</span>
          </div>
        </div>

        {/* ── HAKKINDA ── */}
        <AboutSubMeterSection />

        {/* ── HESAPLAMA ARACI ── */}
        <section className="lp-calculator" aria-labelledby="calculator-title">
          <h2 id="calculator-title">⚡ Elektrik faturası paylaşım aracı</h2>
          <FreeConsumptionStepper onCalculationResultReady={handleCalculationResultReady} />
        </section>

        {isCalculationReady && <AdSenseUnit />}

        {/* ── İÇERİK BÖLÜMLERI ── */}
        <WhySimpleRatioIsNotEnoughSection />
        <TieredTariffExampleSection />
        <TimeOfUseExampleSection />
        <LateReadingExampleSection />
        <RecordManagementSection />
        <UseCasesSection />
        <CommonMistakesSection />
        <FaqSection />

        {/* ── GİRİŞ CTA ── */}
        <section className="lp-login-cta" aria-labelledby="login-title">
          <h2 id="login-title">📁 Kayıtlarınızı düzenli tutun</h2>
          <p>
            Sayaçlarınızı tanımlamak, önceki okumaları saklamak ve dönem sonuçlarına yeniden ulaşmak için
            Google hesabınızla giriş yapabilirsiniz.
          </p>
          <button className="lp-btn-primary" onClick={handleLogin} id="login-cta-button">
            Google ile Giriş Yap
          </button>
        </section>

        {/* ── MOBİL UYGULAMA ── */}
        <section className="lp-mobile-app" aria-labelledby="mobile-title">
          <div>
            <span className="lp-mobile-kicker">Mobil uygulama</span>
            <h2 id="mobile-title">Süzme Sayaç cebinizde</h2>
            <p>Sayaç kayıtlarınıza ve hesaplamalarınıza mobil cihazınızdan ulaşın.</p>
          </div>
          <div className="lp-store-links">
            <a
              href="https://apps.apple.com/app/s%C3%BCzme-saya%C3%A7/id6751746732"
              target="_blank" rel="noopener noreferrer"
              aria-label="Süzme Sayaç uygulamasını App Store'da aç"
            >
              <img src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/tr-tr?size=250x83&releaseDate=1727913600" alt="App Store'dan indir" />
            </a>
            <div className="lp-coming-soon" aria-label="Google Play sürümü çok yakında">
              <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" />
              <span>ÇOK YAKINDA</span>
            </div>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer className="lp-footer">
          <nav aria-label="Yasal ve destek bağlantıları">
            <a href="https://suzmesayac.com/privacy">Gizlilik Politikası</a>
            <a href="https://suzmesayac.com/support">Destek</a>
            <a href="mailto:info@suzmesayac.com">İletişim</a>
          </nav>
          <p>© {new Date().getFullYear()} Süzme Sayaç. Tüm hakları saklıdır.</p>
        </footer>

      </main>
    </div>
  );
};

export default LandingPage;
